import { ChangeEvent, FC, MouseEvent as ReactMouseEvent, useCallback, useEffect, useRef, useState } from 'react';
import { CheckCircle, Mic, MicOff, RadioButtonUnchecked, Stop, WarningAmber } from '@mui/icons-material';
import { CircularProgress, IconButton, ToggleButton, ToggleButtonGroup } from '@mui/material';

import { Text } from '../../../shared/uiComponents';
import { CustomizedInput } from '../../../shared/uiComponents/Input';
import { GREEN, ORANGE, PRIMARY, RED, TEXT } from '../../../shared/Helpers/colors';

interface SpeechRecognitionEvent {
	resultIndex: number;
	results: SpeechRecognitionResultList;
}

interface SpeechRecognitionErrorEvent {
	error: string;
	message: string;
}

export interface FollowUpQuestion {
	id: string;
	label: string;
}

interface VoiceTextInputProperties {
	checklistItems?: FollowUpQuestion[];
	disabled?: boolean;
	loading?: boolean;
	onCheckCoverage?: (transcriptSoFar: string) => Promise<Set<string>>;
	onSubmit: (text: string) => Promise<void>;
}

const MIN_SUMMARY_LENGTH = 15;
const MAX_RECORDING_SECONDS = 90;

const formatElapsed = (seconds: number) => {
	const mins = Math.floor(seconds / 60);
	const secs = seconds % 60;

	return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const NUMBER_WORDS = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine'];

// The subtitle must always reflect how many checklist points are actually still
// outstanding — never a hardcoded count — since some may already be filled in
// from a prior save/edit and shouldn't be asked for again.
const buildCaptureSubtitle = (remaining: number, mode: 'voice' | 'text') => {
	const callToAction = mode === 'voice' ? 'tap the mic when ready' : 'start typing when ready';

	if (remaining === 0) return `All key points are covered. Add any updates, then ${callToAction}.`;

	const count = NUMBER_WORDS[remaining] || String(remaining);
	const point = remaining === 1 ? 'point' : 'points';
	const capitalizedCallToAction = callToAction.charAt(0).toUpperCase() + callToAction.slice(1);

	return `Include ${remaining === 4 ? 'these' : 'the remaining'} ${count} ${point}. ${capitalizedCallToAction}.`;
};

interface ChecklistProps {
	items: FollowUpQuestion[];
	coveredIds: Set<string>;
}
const Checklist: FC<ChecklistProps> = ({ items, coveredIds }) => (
	<ul className="voiceTextInput__instructions">
		{items.map((item) => {
			const isCovered = coveredIds.has(item.id);

			return (
				<li key={item.id} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
					{isCovered ? (
						<CheckCircle fontSize="small" style={{ color: GREEN.BASE }} />
					) : (
						<RadioButtonUnchecked fontSize="small" style={{ color: TEXT.QUATERNARY }} />
					)}
					<Text title={item.label} type="psm" color={isCovered ? GREEN.BASE : TEXT.SECONDARY} />
				</li>
			);
		})}
	</ul>
);

interface ModeToggleProps {
	mode: 'voice' | 'text';
	onChange: (e: ReactMouseEvent<HTMLElement>, newMode: 'voice' | 'text' | null) => void;
	disabled: boolean;
}
const ModeToggle: FC<ModeToggleProps> = ({ mode, onChange, disabled }) => (
	<ToggleButtonGroup
		value={mode}
		exclusive
		onChange={onChange}
		size="small"
		className="voiceTextInput__modeToggle"
		disabled={disabled}
	>
		<ToggleButton value="voice">
			<Mic fontSize="small" />
			&nbsp;Voice
		</ToggleButton>
		<ToggleButton value="text">
			Type
		</ToggleButton>
	</ToggleButtonGroup>
);

const PhiWarning: FC<{ mode: 'voice' | 'text' }> = ({ mode }) => (
	<div className="voiceTextInput__phiWarning">
		<WarningAmber fontSize="medium" style={{ color: ORANGE.BASE }} />
		<Text
			title={`PHI Warning: Do not ${mode === 'text' ? 'type' : 'say'} the client's full name or other identifying information${mode === 'voice' ? ' while recording' : ''}.`}
			type="pmd"
			fontWeight="semiBold"
			color={ORANGE.DARK[32]}
		/>
	</div>
);

const VoiceTextInput: FC<VoiceTextInputProperties> = ({
	onSubmit,
	loading = false,
	disabled = false,
	checklistItems = [],
	onCheckCoverage,
}) => {
	const [mode, setMode] = useState<'voice' | 'text'>('voice');
	const [phase, setPhase] = useState<'capture' | 'wrapUp'>('capture');
	const [isRecording, setIsRecording] = useState(false);
	// Tracks "a recording attempt happened this round", independent of whether speech recognition
	// actually transcribed anything. Without this, stopping a recording that produced an empty
	// transcript (e.g. stopped too quickly, or a speech-recognition round-trip hiccup) silently
	// dumps the BT back to the initial "Tap to start recording" screen with no feedback and no way
	// to retry via Re-record/Done — it just looks like the recording vanished.
	const [hasRecordedOnce, setHasRecordedOnce] = useState(false);
	const [transcript, setTranscript] = useState('');
	const [typedSummary, setTypedSummary] = useState('');
	const [elapsedSeconds, setElapsedSeconds] = useState(0);
	const [coveredIds, setCoveredIds] = useState<Set<string>>(new Set());
	const [checkingCoverage, setCheckingCoverage] = useState(false);
	const [wrapUpItems, setWrapUpItems] = useState<FollowUpQuestion[]>([]);
	const [wrapUpTranscript, setWrapUpTranscript] = useState('');
	const [wrapUpRecording, setWrapUpRecording] = useState(false);
	const [error, setError] = useState('');

	const recognitionRef = useRef<any>(null);
	const finalTranscriptRef = useRef('');
	// Tracks whether the BT still wants to be recording. The Web Speech API can silently fire
	// `onend` after a silence gap even with continuous=true — recording must only actually stop
	// on an explicit user action (Stop/Done), so `onend` auto-restarts unless this is false.
	const recordingIntentRef = useRef(false);
	const wrapUpFinalRef = useRef('');
	const wrapUpTranscriptRef = useRef('');
	wrapUpTranscriptRef.current = wrapUpTranscript;

	const captureText = mode === 'voice' ? transcript.trim() : typedSummary.trim();
	// Stable ref so finalizeSubmit/handleDone always read the latest value without
	// needing captureText in their dep arrays (which would cause churn on every
	// recognition result during recording).
	const captureTextRef = useRef(captureText);
	captureTextRef.current = captureText;

	const runCoverageCheck = useCallback(
		async (textSoFar: string): Promise<Set<string>> => {
			if (!onCheckCoverage || !textSoFar.trim()) return new Set<string>();

			setCheckingCoverage(true);

			try {
				const covered = await onCheckCoverage(textSoFar);
				setCoveredIds(covered);

				return covered;
			} finally {
				setCheckingCoverage(false);
			}
		},
		[onCheckCoverage],
	);

	// Recording elapsed-time counter for the "Recording · 0:24" indicator.
	useEffect(() => {
		if (!isRecording) return undefined;

		const interval = setInterval(() => setElapsedSeconds((s) => s + 1), 1000);

		return () => clearInterval(interval);
	}, [isRecording]);

	const startRecognition = useCallback((onInterim: (text: string) => void, onFinalRef: { current: string }) => {
		setError('');

		const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;

		if (!SpeechRecognition) {
			setError('Speech recognition is not supported in this browser. Please use Chrome or Edge, or switch to text mode.');

			return null;
		}

		const recognition = new SpeechRecognition();
		recognition.continuous = true;
		recognition.interimResults = true;
		recognition.lang = 'en-US';

		recognition.onresult = (event: SpeechRecognitionEvent) => {
			let interimTranscript = '';

			for (let i = event.resultIndex; i < event.results.length; i += 1) {
				const result = event.results[i];

				if (result.isFinal) {
					onFinalRef.current = `${onFinalRef.current} ${result[0].transcript}`.trim();
				} else {
					interimTranscript += result[0].transcript;
				}
			}

			onInterim((`${onFinalRef.current} ${interimTranscript}`).trim());
		};

		recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
			if (event.error === 'no-speech') return;
			setError(`Speech recognition error: ${event.error}`);
			recordingIntentRef.current = false;
			setIsRecording(false);
			setWrapUpRecording(false);
		};

		recognition.onend = () => {
			if (recordingIntentRef.current) {
				recognition.start();

				return;
			}
			setIsRecording(false);
			setWrapUpRecording(false);
		};

		recognitionRef.current = recognition;
		recognition.start();

		return recognition;
	}, []);

	const startRecording = useCallback(() => {
		finalTranscriptRef.current = '';
		setElapsedSeconds(0);
		recordingIntentRef.current = true;
		startRecognition(setTranscript, finalTranscriptRef);
		setIsRecording(true);
		setHasRecordedOnce(true);
	}, [startRecognition]);

	const stopRecording = useCallback(() => {
		recordingIntentRef.current = false;

		if (recognitionRef.current) {
			recognitionRef.current.stop();
			recognitionRef.current = null;
		}
		setIsRecording(false);
	}, []);

	useEffect(
		() => () => {
			recordingIntentRef.current = false;

			if (recognitionRef.current) {
				recognitionRef.current.stop();
			}
		},
		[],
	);

	const handleModeChange = useCallback((_: ReactMouseEvent<HTMLElement>, newMode: 'voice' | 'text' | null) => {
		if (!newMode) return;
		if (isRecording) stopRecording();
		setMode(newMode);
	}, [isRecording, stopRecording]);

	const handleReRecord = useCallback(() => {
		if (isRecording) stopRecording();
		setTranscript('');
		finalTranscriptRef.current = '';
		setElapsedSeconds(0);
		setCoveredIds(new Set());
		setHasRecordedOnce(false);
	}, [isRecording, stopRecording]);

	const resetAll = useCallback(() => {
		setTranscript('');
		setTypedSummary('');
		finalTranscriptRef.current = '';
		setElapsedSeconds(0);
		setCoveredIds(new Set());
		setWrapUpItems([]);
		setWrapUpTranscript('');
		wrapUpFinalRef.current = '';
		setPhase('capture');
		setHasRecordedOnce(false);
	}, []);

	const finalizeSubmit = useCallback(async (wrapUpText?: string) => {
		const text = [
			captureTextRef.current ? `Session summary:\n${captureTextRef.current}` : '',
			wrapUpText?.trim() ? `Additional context:\n${wrapUpText.trim()}` : '',
		].filter(Boolean).join('\n\n');

		if (!text.trim()) return;

		try {
			await onSubmit(text.trim());
			resetAll();
		} catch {
			// Keep state so the BT can retry
		}
	}, [onSubmit, resetAll]);

	const handleDone = useCallback(async () => {
		if (isRecording) stopRecording();

		const finalText = captureTextRef.current;

		const covered = finalText.trim() ? await runCoverageCheck(finalText) : new Set<string>();

		const missing = checklistItems.filter((item) => !covered.has(item.id));

		if (missing.length === 0) {
			await finalizeSubmit();

			return;
		}

		setWrapUpItems(missing);
		setPhase('wrapUp');
	}, [isRecording, stopRecording, runCoverageCheck, checklistItems, finalizeSubmit]);

	// Auto-stop the capture recording and advance to wrap-up when the time limit is reached.
	useEffect(() => {
		if (isRecording && elapsedSeconds >= MAX_RECORDING_SECONDS) {
			handleDone();
		}
	}, [elapsedSeconds, isRecording, handleDone]);

	const startWrapUpRecording = useCallback(() => {
		wrapUpFinalRef.current = wrapUpTranscriptRef.current;
		recordingIntentRef.current = true;
		startRecognition(setWrapUpTranscript, wrapUpFinalRef);
		setWrapUpRecording(true);
	}, [startRecognition]);

	const handleTypedSummaryChange = useCallback((e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		setTypedSummary(e.target.value);
	}, []);

	const stopWrapUpRecording = useCallback(() => {
		recordingIntentRef.current = false;

		if (recognitionRef.current) {
			recognitionRef.current.stop();
			recognitionRef.current = null;
		}
		setWrapUpRecording(false);
	}, []);

	const canProceed = captureText.length >= MIN_SUMMARY_LENGTH && !loading && !disabled;
	const showCaptureControls = isRecording || captureText.length > 0 || hasRecordedOnce;
	const captureSubtitle = buildCaptureSubtitle(checklistItems.length, mode);

	if (phase === 'wrapUp') {
		return (
			<div className="voiceTextInput">
				<div className="voiceTextInput__header">
					<Text title="Almost done!" type="plg" fontWeight="semiBold" />
					<Text
						title={`Your recording didn't cover ${wrapUpItems.length === 1 ? 'this topic' : `these ${wrapUpItems.length} topics`}. Record a quick update, or skip.`}
						type="psm"
						color={TEXT.SECONDARY}
					/>
				</div>

				<PhiWarning mode="voice" />

				<Checklist items={wrapUpItems} coveredIds={new Set()} />

				{Boolean(captureText) && (
					<div className="voiceTextInput__transcript voiceTextInput__transcript--highlight">
						<Text title="Your recording" type="pxs" fontWeight="semiBold" color={GREEN.DARK[16]} className="voiceTextInput__transcriptLabel" />
						<Text title={captureText} type="psm" />
					</div>
				)}

				{Boolean(wrapUpTranscript) && (
					<div className="voiceTextInput__transcript">
						<Text title="You added" type="pxs" fontWeight="semiBold" color={TEXT.TERTIARY} className="voiceTextInput__transcriptLabel" />
						<Text title={wrapUpTranscript} type="psm" />
					</div>
				)}

				{loading ? (
					<div className="voiceTextInput__voiceArea--centered">
						<CircularProgress size={28} />
						<Text title="Filling your SOAP note…" type="psm" color={TEXT.TERTIARY} />
					</div>
				) : (
					<>
						<div className="voiceTextInput__voiceArea voiceTextInput__voiceArea--centered">
							{wrapUpRecording ? (
								<IconButton
									onClick={stopWrapUpRecording}
									aria-label="Stop recording"
									className="voiceTextInput__micButton voiceTextInput__micButton--recording"
									size="large"
								>
									<Stop style={{ color: RED.BASE, fontSize: '32px' }} />
								</IconButton>
							) : (
								<IconButton
									onClick={startWrapUpRecording}
									aria-label="Start recording"
									disabled={disabled}
									className="voiceTextInput__micButton"
									size="large"
								>
									<Mic style={{ color: PRIMARY.BASE, fontSize: '32px' }} />
								</IconButton>
							)}
							<Text
								title={wrapUpRecording ? 'Listening...' : 'Tap to add more'}
								type="psm"
								color={wrapUpRecording ? RED.BASE : TEXT.TERTIARY}
							/>
						</div>

						{Boolean(error) && (
							<div className="voiceTextInput__error">
								<MicOff fontSize="small" style={{ color: RED.BASE }} />
								<Text title={error} type="pxs" color={RED.BASE} />
							</div>
						)}

						<div className="voiceTextInput__controls">
							<button
								type="button"
								className="voiceTextInput__secondaryButton"
								onClick={() => finalizeSubmit()}
								disabled={loading}
							>
								Skip, submit now
							</button>
							<button
								type="button"
								className="voiceTextInput__submitButton"
								onClick={() => finalizeSubmit(wrapUpTranscript)}
								disabled={loading || wrapUpRecording || !wrapUpTranscript}
							>
								{loading ? <CircularProgress size={18} style={{ color: '#fff' }} /> : 'Submit →'}
							</button>
						</div>
					</>
				)}
			</div>
		);
	}

	return (
		<div className="voiceTextInput">
			<div className="voiceTextInput__header">
				<Text title="Summarize your session" type="plg" fontWeight="semiBold" />
				<Text title={captureSubtitle} type="psm" color={TEXT.SECONDARY} />
			</div>

			<PhiWarning mode={mode} />

			{Boolean(checklistItems.length) && (mode === 'text' || !showCaptureControls) && <Checklist items={checklistItems} coveredIds={coveredIds} />}

			{mode === 'voice' && (
				<div className="voiceTextInput__voiceArea">
					{showCaptureControls ? (
						<>
							<div className="voiceTextInput__voiceArea--centered">
								<div className={`voiceTextInput__breathingCircle ${isRecording ? 'voiceTextInput__breathingCircle--active' : ''}`} />
								<Text
									title={
										isRecording
											? elapsedSeconds >= MAX_RECORDING_SECONDS - 15
												? `Recording · ${formatElapsed(elapsedSeconds)} — wrap up, ${MAX_RECORDING_SECONDS - elapsedSeconds}s left`
												: `Recording · ${formatElapsed(elapsedSeconds)} · ${formatElapsed(MAX_RECORDING_SECONDS - elapsedSeconds)} left`
											: 'Recording stopped'
									}
									type="psm"
									color={isRecording ? RED.BASE : TEXT.TERTIARY}
								/>
								{checkingCoverage && <CircularProgress size={14} />}
							</div>

							{Boolean(transcript) && (
								<div className="voiceTextInput__transcript">
									<Text title="Heard so far" type="pxs" fontWeight="semiBold" color={TEXT.TERTIARY} className="voiceTextInput__transcriptLabel" />
									<Text title={transcript} type="psm" />
								</div>
							)}

							{!isRecording && !transcript && (
								<Text
									title="Nothing was heard that time. Tap Re-record and try again."
									type="pxs"
									color={RED.BASE}
								/>
							)}

							{Boolean(checklistItems.length) && <Checklist items={checklistItems} coveredIds={coveredIds} />}

							{isRecording ? (
								<div className="voiceTextInput__voiceArea--centered">
									<IconButton
										onClick={stopRecording}
										aria-label="Stop recording"
										className="voiceTextInput__micButton voiceTextInput__micButton--recording"
										size="large"
									>
										<Stop style={{ color: RED.BASE, fontSize: '32px' }} />
									</IconButton>
									<Text title="Tap to end recording" type="psm" color={TEXT.TERTIARY} />
								</div>
							) : (
								<div className="voiceTextInput__controls">
									<button type="button" className="voiceTextInput__secondaryButton" onClick={handleReRecord} disabled={loading}>
										Re-record
									</button>
									<button type="button" className="voiceTextInput__submitButton" onClick={handleDone} disabled={!canProceed}>
										{loading ? <CircularProgress size={18} style={{ color: '#fff' }} /> : 'Done →'}
									</button>
								</div>
							)}
						</>
					) : (
						<div className="voiceTextInput__voiceArea--centered">
							<IconButton
								onClick={startRecording}
								aria-label="Start recording"
								disabled={disabled || loading}
								className="voiceTextInput__micButton"
								size="large"
							>
								<Mic style={{ color: PRIMARY.BASE, fontSize: '32px' }} />
							</IconButton>
							<Text title="Tap to start recording" type="psm" color={TEXT.TERTIARY} />
							<Text title={`Max ${formatElapsed(MAX_RECORDING_SECONDS)}`} type="pxs" color={TEXT.QUATERNARY} />
						</div>
					)}
				</div>
			)}

			{mode === 'text' && (
				<div className="voiceTextInput__textArea">
					<CustomizedInput
						multiline
						minRows={4}
						maxRows={10}
						value={typedSummary}
						disabled={disabled || loading}
						onChange={handleTypedSummaryChange}
						placeholder="e.g. Client was tired but cooperative, making slow progress. Continue current programming. Two tantrums of moderate severity — his brother came home halfway through and broke focus. Mom mentioned poor sleep."
					/>
					{checkingCoverage && <CircularProgress size={14} />}
					<button type="button" className="voiceTextInput__submitButton" onClick={handleDone} disabled={!canProceed}>
						{loading ? <CircularProgress size={18} style={{ color: '#fff' }} /> : 'Done →'}
					</button>
				</div>
			)}

			{Boolean(error) && (
				<div className="voiceTextInput__error">
					<MicOff fontSize="small" style={{ color: RED.BASE }} />
					<Text title={error} type="pxs" color={RED.BASE} />
				</div>
			)}

			{!showCaptureControls && <ModeToggle mode={mode} onChange={handleModeChange} disabled={disabled || loading} />}
		</div>
	);
};

export default VoiceTextInput;
